import { useMemo, useState } from 'react';
import GenerateKeyModal, { type NewApiKey } from './components/GenerateKeyModal';
import RevokeKeyModal from './components/RevokeKeyModal';
import KpiCard from '../components/KpiCard';
import { apiKeys as seedKeys, type ApiKey } from '@/mocks/integrations';

const platformIcons: Record<string, string> = {
  BloomCommerce: 'ri-store-2-line',
  NovaMarket: 'ri-shopping-cart-2-line',
  Kartel: 'ri-stack-line',
  Loop: 'ri-repeat-2-line',
  Vantage: 'ri-bar-chart-grouped-line',
  Cedar: 'ri-plant-line',
  'Test environment': 'ri-flask-line',
};

export default function IntegrationsPage() {
  const [keys, setKeys] = useState<ApiKey[]>(seedKeys);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'All' | 'Active' | 'Revoked'>('All');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  // Stats
  const activeCount = keys.filter((key) => key.status === 'active').length;
  const revokedCount = keys.filter((key) => key.status === 'revoked').length;
  const totalCalls = keys.reduce((sum, key) => sum + key.callsThisMonth, 0);
  const fullAccessCount = keys.filter(
    (key) => key.status === 'active' && key.scopes.length === 3,
  ).length;

  // Filtered keys
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return keys.filter((key) => {
      const matchesQuery =
        !q ||
        key.platform.toLowerCase().includes(q) ||
        key.keyHint.toLowerCase().includes(q);
      const matchesStatus =
        status === 'All' || key.status === status.toLowerCase();
      return matchesQuery && matchesStatus;
    });
  }, [query, status, keys]);

  const stats = [
    {
      id: 'active',
      label: 'Active keys',
      value: activeCount.toString(),
      icon: 'ri-key-2-line',
      tone: 'primary' as const,
      note: `${((activeCount / (keys.length || 1)) * 100).toFixed(0)}% connected`,
    },
    {
      id: 'calls',
      label: 'API calls this month',
      value: totalCalls.toLocaleString(),
      icon: 'ri-pulse-line',
      tone: 'accent' as const,
      note: 'Across all platforms',
    },
    {
      id: 'full',
      label: 'Full-access keys',
      value: fullAccessCount.toString(),
      icon: 'ri-shield-check-line',
      tone: 'secondary' as const,
      note: 'Read, earn & redeem',
    },
    {
      id: 'revoked',
      label: 'Revoked keys',
      value: revokedCount.toString(),
      icon: 'ri-forbid-2-line',
      tone: 'background' as const,
      note: 'Inactive tokens',
    },
  ];

  const handleCopyKey = async (key: ApiKey) => {
    try {
      await navigator.clipboard.writeText(key.keyHint);
      setCopiedKeyId(key.id);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch {
      // fallback
    }
  };

  const handleGenerate = (data: NewApiKey) => {
    const newId = `key-${Date.now()}`;
    const randomHex = Math.random().toString(16).substring(2, 6);
    setKeys((prev) => [
      {
        id: newId,
        platform: data.platform,
        keyHint: `lb_live_${randomHex}••••••`,
        scopes: data.scopes,
        status: 'active',
        lastUsedAt: 'Just now',
        createdAt: 'Just now',
        callsThisMonth: 0,
      },
      ...prev,
    ]);
  };

  const handleRevoke = (id: string) => {
    setKeys((prev) =>
      prev.map((key) =>
        key.id === id ? { ...key, status: 'revoked', callsThisMonth: 0 } : key,
      ),
    );
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* 1. Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-2xl text-foreground-900 tracking-tight">
            Integrations
          </h3>
          <p className="text-sm text-foreground-500 mt-0.5">
            Manage API keys and connected platforms.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setGenerateOpen(true)}
          className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-medium shadow-sm hover:from-primary-600 hover:to-primary-700 hover:shadow hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-add-line text-base"></i>
          <span>Generate key</span>
        </button>
      </div>

      {/* 2. KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <KpiCard
            key={stat.id}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            tone={stat.tone}
            note={stat.note}
          />
        ))}
      </div>

      {/* 3. Search and Filters (Original Style) */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by platform name..."
            className="w-full bg-background-50 border border-background-200/90 rounded-xl pl-9 pr-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 shadow-xs transition-shadow"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
              aria-label="Clear search"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-background-100 border border-background-200/80">
          {(['All', 'Active', 'Revoked'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setStatus(option)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                status === option
                  ? 'bg-primary-500 text-white shadow-xs'
                  : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        {/* Right side: Key count + View toggle */}
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs font-normal text-foreground-500 whitespace-nowrap">
            {filtered.length} of {keys.length} keys
          </span>

          <div className="inline-flex items-center gap-0.5 p-1 rounded-xl bg-background-100 border border-background-200/80 shadow-2xs">
            <button
              type="button"
              onClick={() => setView('cards')}
              className={`w-7 h-7 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer ${
                view === 'cards'
                  ? 'bg-background-50 text-primary-600 font-medium shadow-xs'
                  : 'text-foreground-400 hover:text-foreground-700'
              }`}
              title="Card layout"
              aria-label="Card layout"
            >
              <i className="ri-layout-grid-line"></i>
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              className={`w-7 h-7 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer ${
                view === 'table'
                  ? 'bg-background-50 text-primary-600 font-medium shadow-xs'
                  : 'text-foreground-400 hover:text-foreground-700'
              }`}
              title="Table layout"
              aria-label="Table layout"
            >
              <i className="ri-table-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* 4. Content Display (Cards View or Table View) */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-background-200/90 bg-background-50/95 p-14 flex flex-col items-center text-center shadow-xs">
          <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-3 shadow-inner">
            <i className="ri-key-2-line text-2xl"></i>
          </span>
          <h4 className="font-semibold text-foreground-900 text-base">No API keys found</h4>
          <p className="mt-1 text-sm text-foreground-500 max-w-sm">
            Try adjusting your search query or switching to another status tab.
          </p>
        </div>
      ) : view === 'cards' ? (
        /* MODERN CARD VIEW */
        <div className="flex flex-col gap-3.5">
          {filtered.map((key) => {
            const active = key.status === 'active';
            const icon = platformIcons[key.platform] ?? 'ri-plug-line';
            const isCopied = copiedKeyId === key.id;

            return (
              <div
                key={key.id}
                className={`group rounded-2xl border transition-all duration-200 p-5 shadow-xs flex flex-col gap-3.5 ${
                  active
                    ? 'bg-background-50/95 border-background-200/90 hover:border-primary-300 hover:shadow-md'
                    : 'bg-background-100/40 border-background-200/60 opacity-75'
                }`}
              >
                {/* Top Row: Icon + Platform + Status | Monthly Calls + Revoke */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* Left: Icon & Name */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <span
                      className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-2xl text-lg shadow-2xs transition-transform group-hover:scale-105 ${
                        active
                          ? 'bg-primary-50 text-primary-600 border border-primary-200/70'
                          : 'bg-background-200 text-foreground-400 border border-background-300/70'
                      }`}
                    >
                      <i className={icon}></i>
                    </span>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5">
                        <h4 className="font-semibold text-foreground-900 text-base md:text-lg truncate group-hover:text-primary-600 transition-colors">
                          {key.platform}
                        </h4>
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-2xs ${
                            active
                              ? 'bg-accent-50 text-accent-700 border border-accent-200/70'
                              : 'bg-background-200/80 text-foreground-500 border border-background-300/80'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              active ? 'bg-accent-500 animate-pulse' : 'bg-foreground-400'
                            }`}
                          ></span>
                          {active ? 'Active' : 'Revoked'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Calls Metric + Revoke Button */}
                  <div className="flex items-center gap-4 self-end sm:self-auto shrink-0">
                    <div className="text-right">
                      <p className="font-semibold text-foreground-900 text-base md:text-lg tabular-nums">
                        {key.callsThisMonth.toLocaleString()}
                      </p>
                      <p className="text-xs text-foreground-400">calls this month</p>
                    </div>

                    {active && (
                      <button
                        type="button"
                        onClick={() => setRevokeTarget(key)}
                        className="whitespace-nowrap px-3.5 py-1.5 rounded-xl text-xs font-medium text-rose-600 bg-rose-50/80 hover:bg-rose-500 hover:text-white border border-rose-200 hover:border-rose-500 shadow-2xs transition-all duration-150 cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <i className="ri-forbid-line"></i>
                        <span>Revoke</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Middle Row: Monospace API Key Strip with Copy */}
                <div className="bg-background-100/80 border border-background-200/80 rounded-xl px-3.5 py-2 flex items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <i className="ri-key-2-line text-foreground-400 text-sm"></i>
                    <code className="font-mono text-xs text-foreground-700 tracking-wide truncate select-all">
                      {key.keyHint}
                    </code>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCopyKey(key)}
                    className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all duration-150 cursor-pointer shadow-2xs ${
                      isCopied
                        ? 'bg-accent-500 text-white font-semibold'
                        : 'bg-background-50 hover:bg-primary-50 text-foreground-700 hover:text-primary-700 border border-background-200/90'
                    }`}
                    title="Copy API key hint"
                  >
                    <i className={isCopied ? 'ri-check-line font-bold' : 'ri-file-copy-line'}></i>
                    <span>{isCopied ? 'Copied' : 'Copy Key'}</span>
                  </button>
                </div>

                {/* Bottom Row: Metadata (Dates) on Left · Scopes on Right */}
                <div className="flex flex-wrap items-center justify-between gap-2.5 text-xs text-foreground-500 pt-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1 text-foreground-400 font-normal">
                      <i className="ri-calendar-line"></i> Created {key.createdAt}
                    </span>
                    <span className="text-foreground-300">•</span>
                    <span className="flex items-center gap-1 font-normal">
                      <i className="ri-time-line text-foreground-400"></i> Last active{' '}
                      <span className="text-foreground-700 font-medium">{key.lastUsedAt}</span>
                    </span>
                  </div>

                  {/* Scopes */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-foreground-400 uppercase tracking-wider mr-0.5 font-normal">
                      Scopes:
                    </span>
                    {key.scopes.map((scope) => (
                      <span
                        key={scope}
                        className="px-2 py-0.5 rounded-md bg-background-100 border border-background-200/80 text-foreground-600 font-mono text-[11px] font-normal shadow-2xs"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* MODERN TABLE VIEW */
        <div className="rounded-2xl border border-background-200/90 bg-background-50/95 shadow-sm overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[840px] text-sm border-collapse">
              <thead>
                <tr className="bg-background-100/80 border-b border-background-200/90 text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-500 select-none">
                  <th className="px-5 py-3.5">Platform</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">API Key</th>
                  <th className="px-5 py-3.5">Scopes</th>
                  <th className="px-5 py-3.5 text-right">Calls (Mo)</th>
                  <th className="px-5 py-3.5 text-right">Last Used</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {filtered.map((key) => {
                  const active = key.status === 'active';
                  const icon = platformIcons[key.platform] ?? 'ri-plug-line';
                  const isCopied = copiedKeyId === key.id;

                  return (
                    <tr
                      key={key.id}
                      className={`transition-colors duration-150 ${
                        active ? 'hover:bg-primary-50/20' : 'bg-background-100/30 opacity-75'
                      }`}
                    >
                      {/* Platform */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-xl text-base ${
                              active
                                ? 'bg-primary-50 text-primary-600 border border-primary-200/70'
                                : 'bg-background-200 text-foreground-400'
                            }`}
                          >
                            <i className={icon}></i>
                          </span>
                          <div>
                            <p className="font-semibold text-foreground-900 truncate">{key.platform}</p>
                            <p className="text-[11px] text-foreground-400 font-normal">Created {key.createdAt}</p>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-2xs ${
                            active
                              ? 'bg-accent-50 text-accent-700 border border-accent-200/70'
                              : 'bg-background-200/80 text-foreground-500 border border-background-300/80'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              active ? 'bg-accent-500 animate-pulse' : 'bg-foreground-400'
                            }`}
                          ></span>
                          {active ? 'Active' : 'Revoked'}
                        </span>
                      </td>

                      {/* API Key with Copy */}
                      <td className="px-5 py-4">
                        <div className="inline-flex items-center gap-1.5 bg-background-100/90 border border-background-200 rounded-lg px-2.5 py-1 shadow-2xs">
                          <code className="font-mono text-xs text-foreground-700 font-normal">
                            {key.keyHint}
                          </code>
                          <button
                            type="button"
                            onClick={() => handleCopyKey(key)}
                            className="text-foreground-400 hover:text-primary-600 cursor-pointer ml-1"
                            title="Copy key"
                          >
                            <i
                              className={
                                isCopied ? 'ri-check-line text-accent-600 font-bold' : 'ri-file-copy-line'
                              }
                            ></i>
                          </button>
                        </div>
                      </td>

                      {/* Scopes */}
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {key.scopes.map((scope) => (
                            <span
                              key={scope}
                              className="px-2 py-0.5 rounded bg-background-100 border border-background-200/80 text-foreground-600 font-mono text-[10px] font-normal"
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Calls */}
                      <td className="px-5 py-4 text-right font-semibold text-foreground-900 tabular-nums">
                        {key.callsThisMonth.toLocaleString()}
                      </td>

                      {/* Last Used */}
                      <td className="px-5 py-4 text-right text-xs text-foreground-500 whitespace-nowrap font-normal">
                        {key.lastUsedAt}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right">
                        {active && (
                          <button
                            type="button"
                            onClick={() => setRevokeTarget(key)}
                            className="whitespace-nowrap px-3 py-1.5 rounded-xl text-xs font-medium text-rose-600 bg-rose-50 hover:bg-rose-500 hover:text-white border border-rose-200 hover:border-rose-500 shadow-2xs transition-all duration-150 cursor-pointer"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. Modals */}
      {generateOpen && (
        <GenerateKeyModal
          open={generateOpen}
          onClose={() => setGenerateOpen(false)}
          onGenerate={handleGenerate}
        />
      )}

      {revokeTarget && (
        <RevokeKeyModal
          open={!!revokeTarget}
          apiKey={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onConfirm={(id) => handleRevoke(id)}
        />
      )}
    </div>
  );
}
